import { useEffect, useRef, useCallback } from "react";

/**
 * Wraps the native BroadcastChannel API for cross-tab messaging.
 *
 * On mount: opens a channel with the given name, calls `onMessage` for each
 * message received. Returns a `postMessage` function that no-ops gracefully
 * if BroadcastChannel is unavailable.
 *
 * If BroadcastChannel is unsupported (older browsers), the hook silently
 * becomes a no-op — tabs work independently with no cross-tab sync.
 */
export const useBroadcastChannel = ({ channelName, onMessage }) => {
  const channelRef = useRef(null);
  const handlerRef = useRef(onMessage);

  // Sync the handler ref outside of render so the latest onMessage is
  // always used without re-opening the channel.
  useEffect(() => {
    handlerRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (typeof window.BroadcastChannel !== "function") return undefined;

    let channel;
    try {
      channel = new window.BroadcastChannel(channelName);
      channelRef.current = channel;
    } catch {
      // BroadcastChannel can throw in some sandboxed contexts — fall back to
      // no cross-tab sync.
      return undefined;
    }

    const listener = (event) => {
      if (handlerRef.current) handlerRef.current(event.data);
    };

    channel.addEventListener("message", listener);

    return () => {
      channel.removeEventListener("message", listener);
      channel.close();
      channelRef.current = null;
    };
  }, [channelName]);

  const postMessage = useCallback((message) => {
    try {
      channelRef.current?.postMessage(message);
    } catch {
      // ignore — broadcast failures should never break the app
    }
  }, []);

  return { postMessage };
};
